import { useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectUser } from '../store/selectors/userSelectors';
// import { logoutUser } from '../store/actions/user/userActions';
import { selectIsLoadingByActionTypes } from '../store/selectors/loadingSelectors';
import {
  AUTHENTICATE_USER_LOADING,
  CREATE_PLAIN_USER_LOADING,
  CREATE_USER_LOADING,
  GENERATE_TOKEN_LOADING,
  GET_BASIC_ACCOUNT_LOADING,
} from '../store/actions/user/userActionConstants';
import {
  GET_SOCURE_CAPTURE_LOADING,
  SOCURE_UPLOAD_DOCUMENT_LOADING,
  SOCURE_VERIFICATION_LOADING,
  GET_REQUIRED_DOCUMENTS_LOADING,
} from '../store/actions/documents/documentsActionConstants';
import SectionLoader from './SectionLoader/SectionLoader';
import Logo from './Logo/Logo';
import Paragraph from './Paragraph/Paragraph';
import TextButton from './TextButton/TextButton';
import Back from './Buttons/Back';
import {
  CREATE_APPLICATION_LOADING,
  GET_APPLICATION_LOADING,
} from '../store/actions/application/applicationActionConstants';
import useWindowDimensions from '../hooks/useWindowDimensions';
import config from '../config';
import themeColors from '../assets/styles/themeColors';
import { selectSettings } from '../store/selectors/settingsSelectors';
import AnchorLogo from './AnchorLogo/AnchorLogo';
import { useGetIsImpersonating } from '../util/hooks/useGetIsImpersonating';
import AssistedRegistrationBanner from './AssistedRegistrationBanner/AssistedRegistrationBanner';
import Footer from './Footer/Footer';
import TermsAndPrivacy from './TermsAndPrivacy/TermsAndPrivacy';
import CloseWhiteIcon from './Icons/CloseWhiteIcon';
import CloseIcon from './Icons/CloseIcon';
import ExitConfirmationModal from './Modals/ExitConfirmationModal/ExitConfirmationModal';
import { useSaveExitCrmEvent } from '../util/helpers/crmEventsHelper';
import { mediaBelow } from '../assets/styles/helper';
import { variables } from '../assets/styles/variables';

const getHeaderBackground = () => {
  const { brokerTheme } = config;

  switch (brokerTheme) {
    case 'HardAssetsAlliance':
      return '';

    case 'GoldSilver':
      return;
    case 'LegacyPM':
      return;
    case 'Contoso':
      return;

    default:
      return '';
  }
};

const MainContainerWraper = styled.div`
  min-height: 100%;
  display: grid;
  grid-template-rows: 1fr;
`;

const NavigationCloseWhiteIcon = styled(CloseWhiteIcon)`
  cursor: pointer;
  z-index: 1;
  width: 2.5rem;
  height: 2.5rem;

  /* Media queries */
  @media only screen and (max-width: 500px) {
    width: 2rem;
    height: 2rem;
  }

  @media only screen and (max-width: 420px) {
    width: 1.8rem;
    height: 1.8rem;
  }
`;

const NavigationCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  z-index: 1;
  width: 2.5rem;
  height: 2.5rem;

  /* Media queries */
  @media only screen and (max-width: 500px) {
    width: 2rem;
    height: 2rem;
  }

  @media only screen and (max-width: 420px) {
    width: 1.8rem;
    height: 1.8rem;
  }
`;

const MainContainer = styled.main`
  display: grid;
  grid-template-columns: ${(props) => (props.halfScreen ? '1fr' : '0.3fr')} 1fr;

  @media only screen and (max-width: 992px) {
    display: flex;
    flex-direction: column;
  }
`;

const BackgroundContainer = styled.div`
  ${({ isGS }) => {
    const bgImage = getHeaderBackground();

    if (isGS) {
      return bgImage
        ? `
          background: ${themeColors.colorSidebarBegin} url(${bgImage});
          background-size: contain;
          background-repeat: no-repeat;
          background-position: right bottom;
        `
        : `
          background: ${themeColors.colorSidebarBegin};
        `;
    }

    return bgImage
      ? `
        background: ${themeColors.colorBackgroundSecondaryDark} url(${bgImage});
        background-size: cover;
      `
      : `
        background: ${themeColors.colorBackgroundSecondaryDark};
      `;
  }}

  padding: 1.5rem;

  @media only screen and (max-width: 992px) {
    height: 6.25rem;
  }

  ${mediaBelow(variables.breakpoints.bpLg)} {
    ${({ isGS }) => {
      const bgImage = getHeaderBackground();

      if (isGS) {
        return `
          background: ${themeColors.colorSidebarBegin};
        `;
      }

      return bgImage
        ? `
          background: ${themeColors.colorBackgroundSecondaryDark} url(${bgImage});
        `
        : `
          background: ${themeColors.colorBackgroundSecondaryDark};
        `;
    }}

    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const ContentContainerWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const ContentContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
  flex-grow: 1;

  @media only screen and (max-width: 992px) {
    padding: 1.5rem;
    align-items: flex-start;
    flex-grow: 1;
    flex-direction: column;
  }
`;

const EmailContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: ${(props) =>
    props.isKycProcess && props.width < 420 ? 'space-between' : 'flex-end'};
  padding: 1.5rem 0.5rem 0;
  font-weight: 600;

  @media only screen and (max-width: 500px) {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
`;

const ChildrenContainer = styled.div`
  max-width: ${(props) => (props.halfScreen ? '400px' : '568px')};
  width: 100%;
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 992px) {
    max-width: 100%;
    flex-grow: 1;
  }
`;

const BackContainer = styled.div`
  visibility: ${(props) => (props?.previousPath ? 'visible' : 'hidden')};
  margin-bottom: 0.75rem;
`;

const BackAndLogOutContainer = styled.div`
  visibility: ${(props) =>
    props?.previousPath && props?.showBackButton ? 'visible' : 'hidden'};
`;

const BaseLayout = ({
  children,
  previousPath,
  halfScreen,
  isKycProcess,
  isUpgrade,
  formId,
  setEventType,
  eventType,
  setSaveAndCloseModalOpen,
  dataCy,
}) => {
  const user = useSelector(selectUser);

  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const settings = useSelector(selectSettings);
  const { isSalesAssisted } = useGetIsImpersonating();
  const [
    isExitConfirmationModalOpen,
    setIsExitConfirmationModalOpen,
  ] = useState(false);
  const handleCrmEvent = useSaveExitCrmEvent();

  // const logout = () => {
  //   dispatch(logoutUser());
  // };

  const logoAnchor = () => {
    if (settings.WebsiteUrl) {
      if (String(settings.WebsiteUrl).includes('https://')) {
        return settings.WebsiteUrl;
      }

      return `https://${settings.WebsiteUrl}`;
    }
  };

  const isLoading = useSelector(
    selectIsLoadingByActionTypes([
      CREATE_USER_LOADING,
      AUTHENTICATE_USER_LOADING,
      SOCURE_UPLOAD_DOCUMENT_LOADING,
      GET_SOCURE_CAPTURE_LOADING,
      SOCURE_VERIFICATION_LOADING,
      GET_REQUIRED_DOCUMENTS_LOADING,
      CREATE_APPLICATION_LOADING,
      GENERATE_TOKEN_LOADING,
      GET_APPLICATION_LOADING,
      GET_BASIC_ACCOUNT_LOADING,
      CREATE_PLAIN_USER_LOADING,
    ]),
  );

  return (
    <SectionLoader isLoading={isLoading}>
      <MainContainerWraper data-cy={dataCy}>
        <MainContainer halfScreen={halfScreen}>
          <BackgroundContainer isGS={config.brokerTheme === 'GoldSilver'}>
            <div
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                justifyContent: isEmpty(user) ? 'flex-start' : 'space-between',
              }}
            >
              {width <= 420 ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    justifyContent: isEmpty(user)
                      ? 'flex-start'
                      : 'space-between',
                    position: 'relative',
                  }}
                >
                  {!isEmpty(user) && (
                    <Back
                      path={previousPath}
                      showTitle={false}
                      color={themeColors.colorWhite}
                      isUpgrade={isUpgrade}
                    />
                  )}

                  <div
                    style={{
                      position: isEmpty(user) ? 'static' : 'absolute',
                      left: isEmpty(user) ? 'unset' : '50%',
                      transform: isEmpty(user) ? 'none' : 'translateX(-50%)',
                    }}
                  >
                    <AnchorLogo
                      type="website"
                      value={logoAnchor()}
                      text={<Logo />}
                    />
                  </div>

                  {!isEmpty(user) && !isSalesAssisted && (
                    <NavigationCloseWhiteIcon
                      w="24px"
                      onClick={() => setIsExitConfirmationModalOpen(true)}
                      h="24px"
                      color={themeColors.colorWhite}
                    />
                  )}
                </div>
              ) : (
                <AnchorLogo
                  type="website"
                  value={logoAnchor()}
                  text={<Logo />}
                />
              )}
            </div>
          </BackgroundContainer>
          <ContentContainerWrap>
            {!isEmpty(user) && (
              <EmailContainer isKycProcess={isKycProcess} width={width}>
                {isKycProcess && (
                  <BackAndLogOutContainer
                    previousPath={previousPath}
                    showBackButton={width < 420}
                  >
                    <Back
                      showTitle
                      path={previousPath}
                      color={themeColors.colorTextSecondary}
                      isUpgrade={isUpgrade}
                    />
                  </BackAndLogOutContainer>
                )}

                {!isSalesAssisted && width > 420 && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <TextButton
                      emailVariant
                      onClick={() => setIsExitConfirmationModalOpen(true)}
                    >
                      <Paragraph emailVariant verticalAlign>
                        {t('loginFluent.saveExit')}
                      </Paragraph>
                    </TextButton>
                    <NavigationCloseIcon
                      onClick={() => setIsExitConfirmationModalOpen(true)}
                    />
                  </div>
                )}
              </EmailContainer>
            )}
            <ContentContainer>
              <ChildrenContainer halfScreen={halfScreen}>
                {isKycProcess ? (
                  <BackAndLogOutContainer
                    previousPath={previousPath}
                    showBackButton={width > 420}
                  >
                    <Back
                      path={previousPath}
                      color={themeColors.colorTextSecondary}
                      isUpgrade={isUpgrade}
                    />
                  </BackAndLogOutContainer>
                ) : (
                  width > 420 && (
                    <BackContainer previousPath={previousPath}>
                      <Back
                        path={previousPath}
                        color={themeColors.colorTextSecondary}
                        isUpgrade={isUpgrade}
                      />
                    </BackContainer>
                  )
                )}

                {children}
                {/* {!noLogoBox && <LogoBox />} */}
              </ChildrenContainer>
            </ContentContainer>
          </ContentContainerWrap>
        </MainContainer>
        <TermsAndPrivacy />
        <Footer />
      </MainContainerWraper>
      {/* <AssistedRegistrationBannerContainer> */}
      <AssistedRegistrationBanner
        formId={formId}
        setEventType={setEventType}
        eventType={eventType}
        setSaveAndCloseModalOpen={setSaveAndCloseModalOpen}
      />
      {/* </AssistedRegistrationBannerContainer> */}
      <ExitConfirmationModal
        isOpen={isExitConfirmationModalOpen}
        onClose={setIsExitConfirmationModalOpen}
        saveAndExit={() => {
          window.location.replace(settings.PortalUrl);
        }}
        handleCrmEvent={handleCrmEvent}
        registrationUrl={`${window.location.origin}${window.location.pathname}`}
      />
    </SectionLoader>
  );
};

BaseLayout.defaultProps = {
  isKycProcess: false,
};

BaseLayout.propTypes = {
  children: PropTypes.node,
  previousPath: PropTypes.string,
  halfScreen: PropTypes.bool,
  isKycProcess: PropTypes.bool,
  isUpgrade: PropTypes.func,
  formId: PropTypes.string,
  setEventType: PropTypes.func,
  eventType: PropTypes.string,
  setSaveAndCloseModalOpen: PropTypes.func,
  dataCy: PropTypes.string,
};

export default BaseLayout;
